"use client";

import { m, useScroll, useTransform } from "framer-motion";
import { Minus } from "lucide-react";
import { useRef } from "react";
import type { BrandStatement as BrandStatementData } from "@/lib/validations/cms";
import EditableText from "@/components/edit-mode/EditableText";
import { useEditMode } from "@/contexts/EditModeContext";
import BrandSectionItem from "./BrandSectionItem";

export default function BrandStatement({ data }: { data: BrandStatementData }) {
  const containerRef = useRef(null);
  const { isEditing } = useEditMode();
  const showGrid = !data.disableSquaredPattern;
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  const x1 = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
  const x2 = useTransform(scrollYProgress, [0, 1], ["-20%", "0%"]);

  return (
    <section
      ref={containerRef}
      className="bg-foreground text-background relative overflow-hidden py-16 md:py-32"
    >
      <div className="relative z-10 mx-auto max-w-[1920px] px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl">
          <m.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="text-muted-foreground mb-8 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase"
          >
            <Minus className="h-4 w-4" strokeWidth={1} />
            <EditableText section="brandStatement" field="label" value={data.label} />
          </m.div>

          <m.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mb-12 text-4xl leading-[0.9] font-black tracking-tighter text-balance uppercase md:mb-16 md:text-6xl lg:text-7xl"
          >
            <m.span
              className="inline-block"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              <EditableText section="brandStatement" field="titlePrefix" value={data.titlePrefix} />
            </m.span>
            <m.span
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="text-muted-foreground relative inline-block"
            >
              <EditableText
                section="brandStatement"
                field="titleHighlight"
                value={data.titleHighlight}
              />
              <m.span
                className="bg-muted-foreground absolute bottom-0 left-0 h-[2px] w-full"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              />
            </m.span>
            <m.span
              className="inline-block"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              {" "}
              <EditableText section="brandStatement" field="titleSuffix" value={data.titleSuffix} />
            </m.span>
          </m.h2>

          <div className="grid grid-cols-1 gap-12 px-4 md:grid-cols-2 md:gap-20 md:px-6">
            {data.sections.map((section, index) => (
              <BrandSectionItem key={index} section={section} index={index} />
            ))}
          </div>
        </div>
      </div>

      {showGrid && (
        <div className="pointer-events-none absolute inset-0 z-0 bg-[linear-gradient(to_right,var(--background)_1px,transparent_1px),linear-gradient(to_bottom,var(--background)_1px,transparent_1px)] bg-[size:40px_40px] opacity-10"></div>
      )}

      <div
        className={`absolute top-20 left-0 w-full overflow-hidden opacity-[0.03] select-none ${isEditing ? "" : "pointer-events-none"}`}
      >
        <m.div style={{ x: x1 }} className="whitespace-nowrap">
          <EditableText
            section="brandStatement"
            field="marqueeBackground1"
            value={data.marqueeBackground1}
          />
        </m.div>
      </div>
      <div
        className={`absolute bottom-20 left-0 w-full overflow-hidden opacity-[0.03] select-none ${isEditing ? "" : "pointer-events-none"}`}
      >
        <m.div style={{ x: x2 }} className="whitespace-nowrap">
          <EditableText
            section="brandStatement"
            field="marqueeBackground2"
            value={data.marqueeBackground2}
          />
        </m.div>
      </div>
    </section>
  );
}
